import {
  PdfGen,
  PdfSize,
  PdfUnit,
  PdfRect,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageSingleGen } from '../layoutImageSingle';
import { IItemCount } from '@educraft/base';
import { OptsImageSingle } from '../optsImageSingle';
import { GUtil } from '../../pdf/gutil';
import { genSingle, fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

/** Approximate the C# HSL rainbow image using thin coloured vertical stripes. */
function drawRainbow(page: PageGen, rect: PdfRect, stripes = 48): void {
  const stripeW = rect.width / stripes;
  for (let i = 0; i < stripes; i++) {
    const hue = (i / stripes) * 720; // double rainbow cycle (0..720)
    const { r, g, b } = hslToRgb(hue, 1, 0.9);
    const color = PdfColor.fromRgb(r, g, b);
    const stripeRect = new PdfRect(rect.x + i * stripeW, rect.y, stripeW + 1, rect.height);
    page.drawRectangle(null, new PdfBrush(color), stripeRect);
  }
}

/**
 * Lepitka — cut-out sticker grid.
 *
 * For each uploaded image a pair of pages is produced:
 *   Page 1 (answers): rainbow gradient + centred image + 4×4 grid of answer stickers.
 *   Page 2 (questions): 4×4 grid of question stickers + border.
 *
 * Print both pages, cut along the grid lines. Place answer stickers
 * face-down so the rainbow shows; student flips after solving the question.
 *
 * Port of: EduFrame.ImgSingle.Generators.Lepitka
 */
export class Lepitka implements ILayoutImageSingleGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImageSingle): Promise<void> {
    const {A4} = PdfSize;
    const margin = PdfUnit.FromMillimeters(5);
    const count = 4;

    // Square centred vertically on portrait A4
    const squareSz = A4.width - 2 * margin;
    const yPos = (A4.height - 2 * margin - A4.width - 2 * margin) / 2;
    const mainRect = new PdfRect(margin, yPos, squareSz, squareSz);

    const totalCount = Math.min(opts.list.length, this.countMax());

    for (let i = 0; i < totalCount; i++) {
      const imgItem = opts.list[i];

      let array;
      try {
        array = genSingle(opts.data, opts.generator, count * count, false, true);
      } catch {
        continue; // skip image if generator cannot produce enough unique items
      }

      const page1 = pdf.addPage(); // answers page
      const page2 = pdf.addPage(); // questions page

      // Page 1: rainbow gradient background (approximated with coloured stripes)
      drawRainbow(page1, mainRect);

      // Centred image on top of gradient (page 1 only)
      if (imgItem?.image) {
        page1.drawImageCenter(imgItem.image, mainRect);
      }

      // Page 2: white background (no rainbow, no image)
      page2.drawRectangle(null, PdfBrush.White, mainRect);

      page1.drawRectangle(GUtil.basePen, null, mainRect);

      // 4×4 sticker grid
      const cellW = mainRect.width / count;
      const cellH = mainRect.height / count;

      for (let cx = 0; cx < count; cx++) {
        for (let cy = 0; cy < count; cy++) {
          const index = cx + cy * count;
          const {answer} = array[index];
          const question = fixGenArray(array[index].texts).join(' ');

          const r = new PdfRect(mainRect.x + cx * cellW, mainRect.y + cy * cellH, cellW, cellH);

          page1.drawRectangle(GUtil.cutPen, null, r);
          page2.drawRectangle(GUtil.cutPen, null, r);

          const f1 = page1.adjustFont(r.width * 0.8, [answer], GUtil.baseFont);
          page1.drawString(answer, f1, PdfBrush.Black, null, r, ETextAlign.Center);

          const f2 = page2.adjustFont(r.width * 0.8, [question], GUtil.baseFont);
          page2.drawString(question, f2, PdfBrush.Black, null, r, ETextAlign.Center);
        }
      }

      page2.drawRectangle(GUtil.basePen, null, mainRect);
    }
  }
}
